import { AcquisitionFlow, getAcquisitionFlow, getAppData, getProgramData } from '../../../utils/acquisition-utils';
import SessionStorage from '../../../utils/SessionStorage';
import React, { useEffect, useRef, useState } from 'react';
import { APP_VERIFICATION_EVENT } from '../ApplicationVerification';
import { Row, Col } from '../../../importCL';
import { useAcquisitionContext } from '../../../providers/AcquisitionProvider/AcquisitionProvider';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';

interface ICardDesignFormProps {
    defaultCardImagePath?: string;
    premiumPrompt?: string;
    isInEdit?: boolean;
}

interface IChosenCard {
    path: string;
    name: string;
    fee: string;
    code?: string;
}

interface ICard {
    Selected: boolean;
    CardName: string;
    CardDescription: string;
    PremiumFee: number;
    PlasticId: number;
    IsFeeRecurring: boolean;
    PlasticDesc?: string;
    IsPremium: boolean;
    ProgramPlasticCode: number;
    Code: string;
}

const getCardImage = async (defaultCardPath: string, cards : any[]): Promise<IChosenCard> => {
    const appData = await getAppData();
    const selectedCard = appData.SelectedCardId ? parseInt(appData.SelectedCardId) : parseInt(appData.SelectedProgramPlasticCode);

    if (isNaN(selectedCard)) {
        return {
            path: defaultCardPath,
            name: '',
            fee: '',
        };
    }

    if (!cards || !cards.length) {
        return {
            path: defaultCardPath,
            name: '',
            fee: '',
        };
    }

    for (const card of cards) {
        if (parseInt(card.ProgramPlasticCode) === selectedCard) {
            const cardPath = `/content/dam/cob-corp-acquisition/images/cards/app-card-images/${card.Code}.png`;
            SessionStorage.set('selectedCardImgPath', cardPath);
            return {
                path: cardPath,
                name: card.CardName,
                fee: card.IsPremium && card.PremiumFee > 0 ? `$${card.PremiumFee}` : 'Free',
                code: card.Code,
            };
        }
    }
    return {
        path: defaultCardPath,
        name: '',
        fee: '',
        code: '',
    };
};

export const CardDesignForm = (props: ICardDesignFormProps): JSX.Element => {
    const {hidingPlasticCardDesign} = useAcquisitionContext();
    const [cardImage, setCardImage] = useState<IChosenCard>({} as IChosenCard);
    const [cardsAreAllFree, setCardsAreAllFree] = useState<boolean>();

    const cardsRef = useRef<Array<ICard>>([]);
    
    useEffect(() => {
        getProgramData().then(async (programData) => {
            let cards: ICard[] = [];
            getAppData().then(async (appData) => {
                if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                    cards = programData?.offers[0]?.card?.keyCardFeatures?.cardVariations?.map((c, i) => ({
                        Selected: appData.SelectedCardId === (i + 1),
                        CardName: c.name,
                        CardDescription: c.description,
                        PremiumFee: c.premiumFee,
                        PlasticId: i + 1,
                        IsFeeRecurring: c.feeRecurring,
                        IsPremium: c.premiumFee && c.premiumFee > 0,
                        ProgramPlasticCode: i + 1,
                        Code: c.cardMaterialId,
                    })) ?? [];
                } else {
                    cards = programData.CardDesign ? programData.CardDesign.Cards : [];
                }
                cardsRef.current = cards;
                setCardImage(await getCardImage(props.defaultCardImagePath || '', cards));
                setCardsAreAllFree(!cards.some( (card) => card.IsPremium));
            });
        });

        const cardEditEventListener = () => {
            if (!props.isInEdit && cardsRef.current.length) {
                getCardImage(props.defaultCardImagePath || '', cardsRef.current).then(card => {
                    setCardImage(card);
                });
            }
        };

        window.addEventListener(APP_VERIFICATION_EVENT, cardEditEventListener);
        return () => window.removeEventListener(APP_VERIFICATION_EVENT, cardEditEventListener);
    }, []);

    return (
        <div className={`${hidingPlasticCardDesign ? 'PersonalInformation--hidden': ''}`}>
            <Row className='pb-4'>
                <Col xs={3} md={6}>
                    <figure className='PersonalInformation__cardImageWrapper'>
                        <div className='PersonalInformation__cardContainer'>
                            <img loading='lazy' className='PersonalInformation__cardImage' src={cardImage.path} alt={cardImage.name} />
                        </div>
                    </figure>
                </Col>
                <Col xs={3} md={6}>
                    <h4 className='PersonalInformation__cardName'>{cardImage.name}&nbsp;<b>({cardImage.fee})</b></h4>
                </Col>
                { props.premiumPrompt && cardsAreAllFree !== true && cardImage.fee === 'Free' &&
                <div className= 'PersonalInformation__premiumPrompt'>
                    {transformHTMLParserLinks(props.premiumPrompt)}
                </div>
                }
            </Row>
        </div>
    );
};